import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { tenantAutoPayFailedNotificationRepository } from '~/repositories/settings/tenantAutoPayFailedNotificationRepository';
import type { Accessor } from 'solid-js';
import type {
  TenantAutoPayFailedNotificationSettings,
  TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument,
} from '~/swagger/settings/Api';

interface TenantAutoPayFailedNotificationContextValue {
  allTenantAutoPayFailedNotificationSettings: Accessor<
    TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
  >;
  getTenantAutoPayFailedNotificationSettings: () => Promise<
    TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
  >;
  updateTenantAutoPayFailedNotificationSettings: (payload: TenantAutoPayFailedNotificationSettings) => Promise<void>;
  updateTenantAutoPayFailedNotificationSettingsByLevel: (
    level: string,
    levelId: string,
    payload: TenantAutoPayFailedNotificationSettings
  ) => Promise<void>;
  deleteTenantAutoPayFailedNotificationSettingsByLevel: (level: string, levelId: string) => Promise<void>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | undefined>;
}

export const [TenantAutoPayFailedNotificationProvider, useTenantAutoPayFailedNotification] =
  createMagicDoorContext<TenantAutoPayFailedNotificationContextValue>('TenantAutoPayFailedNotification', () => {
    const repo = new tenantAutoPayFailedNotificationRepository();

    const [isLoading, setIsLoading] = createSignal<boolean>(false);
    const [error, setError] = createSignal<Error | undefined>(undefined);
    const [allTenantAutoPayFailedNotificationSettings, setAllTenantAutoPayFailedNotificationSettings] = createSignal<
      TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    >(undefined);

    const getTenantAutoPayFailedNotificationSettings = async (): Promise<
      TenantAutoPayFailedNotificationSettingsCompanyPortalTypedSettingsWithOverridesDocument | undefined
    > => {
      setIsLoading(true);
      try {
        const result = await repo.getTenantAutoPayFailedNotificationAll();
        setAllTenantAutoPayFailedNotificationSettings(result);
        return result;
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    const updateTenantAutoPayFailedNotificationSettings = async (payload: TenantAutoPayFailedNotificationSettings): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.updateTenantAutoPayFailedNotification(payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const updateTenantAutoPayFailedNotificationSettingsByLevel = async (
      level: string,
      levelId: string,
      payload: TenantAutoPayFailedNotificationSettings
    ): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.updateTenantAutoPayFailedNotificationByLevel(level, levelId, payload);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        throw err;
      } finally {
        setIsLoading(false);
      }
    };

    const deleteTenantAutoPayFailedNotificationSettingsByLevel = async (level: string, levelId: string): Promise<void> => {
      setIsLoading(true);
      try {
        await repo.deleteTenantAutoPayFailedNotificationByLevel(level, levelId);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    };

    return {
      allTenantAutoPayFailedNotificationSettings,
      getTenantAutoPayFailedNotificationSettings,
      updateTenantAutoPayFailedNotificationSettings,
      updateTenantAutoPayFailedNotificationSettingsByLevel,
      deleteTenantAutoPayFailedNotificationSettingsByLevel,
      isLoading,
      error,
    };
  });
